import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaGlobe, FaChevronDown } from "react-icons/fa";
import { normalizeLanguage } from "../i18n";

function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const languages = [
    { code: "en", name: "English", flag: "🇺🇸" },
    { code: "ur", name: "اردو", flag: "🇵🇰" },
    { code: "sd", name: "سنڌي", flag: "🇵🇰" },
  ];

  const resolved = normalizeLanguage(i18n.language);
  const currentLanguage = languages.find((lang) => lang.code === resolved) || languages[0];

  const changeLanguage = (lng) => {
    const code = normalizeLanguage(lng);
    i18n.changeLanguage(code);
    localStorage.setItem("language", code);
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen]);

  return (
    <div className="relative shrink-0">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((o) => !o)}
        className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-2 rounded-lg border border-white/25 bg-white/10 text-white hover:bg-white/20 transition-all duration-200 text-xs sm:text-sm font-medium max-w-[9rem] sm:max-w-none"
      >
        <FaGlobe className="text-sm shrink-0 text-orange-50" aria-hidden />
        <span className="truncate">
          <span className="sm:hidden" aria-hidden>
            {currentLanguage.flag}
          </span>
          <span className="hidden sm:inline">
            {currentLanguage.flag} {currentLanguage.name}
          </span>
        </span>
        <FaChevronDown
          className={`text-[10px] sm:text-xs shrink-0 text-orange-100 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>

      {isOpen ? (
        <>
          <div
            className="fixed inset-0 z-40"
            aria-hidden
            onClick={() => setIsOpen(false)}
          />
          <div
            className="absolute top-full right-0 mt-2 w-48 rounded-lg border border-slate-200 bg-white py-2 shadow-lg dark:border-slate-600 dark:bg-slate-800 z-[60]"
            role="listbox"
            aria-label="Language"
          >
            {languages.map((language) => (
              <button
                key={language.code}
                type="button"
                role="option"
                aria-selected={resolved === language.code}
                onClick={() => changeLanguage(language.code)}
                className={`flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition-colors duration-150 ${
                  resolved === language.code
                    ? "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                    : "text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700"
                }`}
              >
                <span className="text-lg leading-none" aria-hidden>
                  {language.flag}
                </span>
                <span className="font-medium">{language.name}</span>
                {resolved === language.code ? (
                  <span className="ml-auto text-blue-600 dark:text-blue-400" aria-hidden>
                    ✓
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

export default LanguageSwitcher;
